import * as React from 'react';
import { useCallback } from 'react';
import { useAssistanceContext } from '../../../document/pages/Assistance/AssistanceContext.tsx';
import { AssistanceViewContextProvider } from '../../../document/pages/Assistance/AssistanceViewContext.tsx';
import AssistanceSectionEmptyState from '../../../document/pages/Assistance/AssistanceSectionEmptyState.tsx';
import VirtualizedItemList, {
    VirtualizedItemListHandle,
} from '../../../document/pages/Assistance/VirtualizedItemList.tsx';
import { ItemDataSectionWithDragDrop } from '../pages/Assistance.tsx';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../../core/components/Icon.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const LoadingIconSpinner = withIcon(faSpinnerThird);

const POSITION_VIRTUALIZATION_THRESHOLD = 30;

interface ArticlesContentProps {
    articlesByPosition: any[];
    loading: boolean;
    chapterBeingLoaded: string | undefined;
    chapter: string;
    articlesViewMode: string;
    positionsScrollRef: React.RefObject<HTMLDivElement>;
    formsRef: React.RefObject<HTMLDivElement>;
    articlesScrollRef: React.RefObject<HTMLDivElement>;
    onArticleAdded: (chapter: string, options?: { updateNumbers?: boolean }) => Promise<void> | void;
    onEnsureChapterLoaded: (field: any) => void;
    chapterMap?: Map<string, any>;
    onChapterHeaderClick?: (params: { openChapter: boolean; chapter: string }) => Promise<void>;
    positionExpandFunctionsRef?: React.RefObject<Map<number, () => void>>;
    positionVirtualizerRef?: React.RefObject<VirtualizedItemListHandle | null>;
    selectedArticleIds?: Set<string>;
}

export const ArticlesContent: React.FC<ArticlesContentProps> = ({
    articlesByPosition,
    loading,
    chapterBeingLoaded,
    chapter,
    articlesViewMode,
    positionsScrollRef,
    formsRef,
    articlesScrollRef,
    onArticleAdded,
    onEnsureChapterLoaded,
    chapterMap,
    onChapterHeaderClick,
    positionExpandFunctionsRef,
    positionVirtualizerRef,
    selectedArticleIds,
}) => {
    const { handlers } = useAssistanceContext();

    const handleUpdate = ({ fieldName, payload, action, itemIndex }) => {
        if (action) {
            return handlers.onUpdate({ action, itemIndex, payload });
        }

        let updatePayload = payload?.data?.updatePayload ?? payload?.updatePayload;
        let targetArticleSet = null;

        if (!updatePayload) {
            for (const group of articlesByPosition) {
                const article = group.articles.find((a) => a.fields[0]?.itemIndex === itemIndex);
                if (article) {
                    const field = article.fields.find((f) => f.fieldName === fieldName);
                    if (field?.updatePayload) {
                        updatePayload = field.updatePayload;
                        targetArticleSet = group.articleSets.find((set) => set.setIndex === article.setIndex);
                        break;
                    }
                }
            }
        } else {
            // if updatePayload exists, find the article set from articlesByPosition
            for (const group of articlesByPosition) {
                const article = group.articles.find(
                    (a) => a.setIndex === updatePayload.setIndex && a.articleIndex === updatePayload.articleIndex
                );
                if (article) {
                    targetArticleSet = group.articleSets.find((set) => set.setIndex === article.setIndex);
                    break;
                }
            }
        }

        if (!updatePayload) {
            console.error('No updatePayload found for field:', fieldName, itemIndex);
            return Promise.resolve();
        }

        // if the article set is complete and user edits it, it needs to be marked as incomplete again
        const shouldMarkIncomplete = targetArticleSet?.isComplete;

        return handlers
            .onUpdate({
                action: 'action:change_article_field',
                itemIndex: updatePayload.itemIndex,
                payload: {
                    setIndex: updatePayload.setIndex,
                    articleIndex: updatePayload.articleIndex,
                    indexInChapter: updatePayload.indexInChapter,
                    fieldName,
                    value: payload.value,
                    ...payload,
                    ...(shouldMarkIncomplete ? { _markIncomplete: true } : {}),
                },
            })
            .then((result) => {
                if (onArticleAdded) {
                    onArticleAdded(chapter, { updateNumbers: !!shouldMarkIncomplete });
                }
                return result;
            });
    };

    const shouldVirtualizePositions = articlesByPosition.length > POSITION_VIRTUALIZATION_THRESHOLD;

    const renderPositionGroup = useCallback(
        (index: number) => {
            const positionGroup = articlesByPosition[index];
            if (!positionGroup) return null;

            return (
                <ItemDataSectionWithDragDrop
                    positionGroup={positionGroup}
                    articlesViewMode={articlesViewMode}
                    positionsScrollRef={positionsScrollRef}
                    onArticleAdded={(options?: { updateNumbers?: boolean }) =>
                        onArticleAdded(positionGroup.position.chapter, options)
                    }
                    onEnsureChapterLoaded={onEnsureChapterLoaded}
                    chapterMap={chapterMap}
                    onChapterHeaderClick={onChapterHeaderClick}
                    positionExpandFunctionsRef={positionExpandFunctionsRef}
                    handleUpdate={handleUpdate}
                    selectedArticleIds={selectedArticleIds}
                />
            );
        },
        [
            articlesByPosition,
            articlesViewMode,
            positionsScrollRef,
            onArticleAdded,
            onEnsureChapterLoaded,
            chapterMap,
            onChapterHeaderClick,
            positionExpandFunctionsRef,
            handleUpdate,
            selectedArticleIds,
        ]
    );

    return (
        <div className={classnames(articlesByPosition.length === 0 && 'p-8')} ref={formsRef}>
            <AssistanceViewContextProvider
                onUpdate={handleUpdate}
                onReselect={() => Promise.resolve()}
                onBeforeFieldUpdate={onEnsureChapterLoaded}
                formsRef={formsRef}
                previewRef={articlesScrollRef}
            >
                {loading && chapterBeingLoaded === chapter ? (
                    <div className="flex items-center justify-center p-12">
                        <LoadingIconSpinner spin className="text-content-brand-strong text-2xl" />
                    </div>
                ) : articlesByPosition.length === 0 ? (
                    <AssistanceSectionEmptyState
                        title="listOfServicesView.emptyState.title"
                        description="listOfServicesView.emptyState.description"
                        className="rounded-lg"
                    />
                ) : shouldVirtualizePositions ? (
                    <VirtualizedItemList
                        ref={positionVirtualizerRef}
                        count={articlesByPosition.length}
                        scrollContainerRef={articlesScrollRef}
                        renderItem={renderPositionGroup}
                        estimateSize={300}
                        overscan={5}
                    />
                ) : (
                    articlesByPosition.map((positionGroup) => (
                        <ItemDataSectionWithDragDrop
                            key={positionGroup.positionIndex}
                            positionGroup={positionGroup}
                            articlesViewMode={articlesViewMode}
                            positionsScrollRef={positionsScrollRef}
                            onArticleAdded={(options?: { updateNumbers?: boolean }) =>
                                onArticleAdded(positionGroup.position.chapter, options)
                            }
                            onEnsureChapterLoaded={onEnsureChapterLoaded}
                            chapterMap={chapterMap}
                            onChapterHeaderClick={onChapterHeaderClick}
                            positionExpandFunctionsRef={positionExpandFunctionsRef}
                            handleUpdate={handleUpdate}
                            selectedArticleIds={selectedArticleIds}
                        />
                    ))
                )}
            </AssistanceViewContextProvider>
        </div>
    );
};
